import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@apunte-digital/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copies a pasteable diagnostic report to the clipboard.
 *
 * The component only COLLECTS data and delegates formatting to
 * `buildDiagnosticsReport` (shared, pure and tested). If the report were
 * assembled here, every app would have its own version and they would diverge
 * as soon as someone added a field.
 *
 * Labels: `Copy` and `Copied!` are used, and they are already translated into the
 * eleven languages. A new string like "Copy diagnostics" would appear in ENGLISH
 * in all of them without the i18n gate complaining, because it only validates
 * what is already in the catalog.
 */
/** How long "Copied!" remains before returning to the normal label. */
const MS_CONFIRMACION = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** The SAME model that the screen renders. */
  secciones: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const [copiado, setCopiado] = createSignal(false);
  const [error, setError] = createSignal(false);

  const copiar = async () => {
    const texto = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: props.secciones(),
    });
    try {
      await navigator.clipboard.writeText(texto);
      setError(false);
      setCopiado(true);
      setTimeout(() => setCopiado(false), MS_CONFIRMACION);
    } catch {
      // The clipboard fails without permission or outside a secure context. Staying
      // silent would leave the user believing they copied it: mark the failure and leave
      // the report selectable as plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void copiar()}
      >
        <Show when={copiado()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {copiado() ? t(msg`Copied!`) : t(msg`Copy`)}
      </Button>

      {/* Plan B when the clipboard is unavailable: the report on screen
          para seleccionarlo a mano. Sin esto, el botón sería un callejón sin
          salida justo en los entornos donde más falta hace depurar. */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: props.secciones(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
